import { DollarSign, BarChart, Download, Wallet, ArrowUpRight } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/utils';
import { SalesStats } from './types';
import { useLogger } from '@/lib/axiom/client';

interface StatsCardsProps {
  stats: SalesStats;
  onExportData: () => void;
}

export function StatsCards({ stats, onExportData }: StatsCardsProps) {
  const router = useRouter();
  const log = useLogger();

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</div>
          <div className="flex justify-between items-center mt-1">
            <p className="text-xs text-muted-foreground">Net: {formatCurrency(stats.netRevenue)}</p>
            <p className="text-xs text-muted-foreground">
              Fees: {formatCurrency(stats.platformFee)}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <CardTitle className="text-sm font-medium">Total Sales</CardTitle>
            <BarChart className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalSales}</div>
          <p className="text-xs text-muted-foreground">
            Avg. Order: {formatCurrency(stats.averageOrderValue)}
          </p>
        </CardContent>
      </Card>

      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <CardTitle className="text-sm font-medium">Available for Payout</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatCurrency(stats.availableForPayout)}</div>
          <div className="flex justify-between items-center mt-1">
            <p className="text-xs text-muted-foreground">
              Pending: {formatCurrency(stats.pendingPayouts)}
            </p>
            <p className="text-xs text-muted-foreground">
              Min: {formatCurrency(stats.minimumPayoutAmount)}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="hover:shadow-md transition-shadow">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Manage your sales and reports</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* <Button className="w-full flex items-center gap-2" onClick={onExportData}>
            <Download className="h-4 w-4" />
            <span>Export Sales Data</span>
          </Button> */}
          <Button
            variant={stats.canRequestPayout ? 'default' : 'outline'}
            className="w-full flex items-center gap-2"
            disabled={!stats.canRequestPayout}
            onClick={() => {
              log.info('Request Payout Clicked');
              router.push('/dashboard/payouts');
            }}
          >
            <ArrowUpRight className="h-4 w-4" />
            <span>
              {stats.canRequestPayout
                ? `Request Payout (${formatCurrency(stats.availableForPayout)})`
                : `Min. ${formatCurrency(stats.minimumPayoutAmount)} Required`}
            </span>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
